/**
 * "Where this booking came from" — the ad attribution captured on arrival.
 *
 * Sits directly above the Google Ads Conversion card so the two read as one
 * story: which ad produced this booking, and whether we managed to tell Google
 * about it.
 *
 * The landing URL is the source of truth — gbraid, wbraid, msclkid and fbclid
 * have no columns of their own and are parsed back out of it here.
 */

import { useState } from 'react'
import { Check, ChevronDown, ChevronRight, Copy, Megaphone } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { useToast } from '@/hooks/use-toast'
import {
  describeTrafficSource,
  hasAttribution,
  otherPlatformClickIds,
  resolveStoredClickId,
  summarizeLandingUrl,
  utmPairs,
  type AttributionFields,
} from '@/lib/attribution/display'

interface TrafficSourceCardProps {
  booking: AttributionFields & { visitor_id?: string | null; session_id?: string | null }
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5 sm:flex-row sm:items-baseline sm:gap-3">
      <span className="w-32 shrink-0 text-sm text-muted-foreground">{label}</span>
      <span className="break-all text-sm">{value}</span>
    </div>
  )
}

export function TrafficSourceCard({ booking }: TrafficSourceCardProps) {
  const { toast } = useToast()
  const [expanded, setExpanded] = useState(false)
  const [copied, setCopied] = useState(false)

  const landing = summarizeLandingUrl(booking.landing_url)
  const clickId = resolveStoredClickId(booking)
  const utms = utmPairs(booking)
  const otherIds = otherPlatformClickIds(booking.landing_url)

  const copyLandingUrl = async () => {
    if (!landing) return
    try {
      await navigator.clipboard.writeText(landing.full)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      toast({
        title: 'Could not copy',
        description: 'Your browser blocked clipboard access.',
        variant: 'destructive',
      })
    }
  }

  if (!hasAttribution(booking)) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Megaphone className="h-5 w-5" />
            Traffic Source
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            No attribution recorded. Expected for direct or organic visits, for bookings
            taken before attribution tracking was added, and for any booking created by
            an operator rather than the website.
          </p>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Megaphone className="h-5 w-5" />
          Traffic Source
        </CardTitle>
      </CardHeader>

      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={clickId ? 'default' : 'secondary'}>
            {describeTrafficSource(booking)}
          </Badge>
        </div>

        {clickId && (
          <div className="space-y-1.5">
            <Row
              label="Click ID"
              value={
                <span className="flex flex-wrap items-baseline gap-2">
                  <code className="rounded bg-muted px-1.5 py-0.5 text-xs">
                    {clickId.type}
                  </code>
                  <span className="break-all font-mono text-xs">{clickId.value}</span>
                </span>
              }
            />
            <p className="text-xs text-muted-foreground sm:pl-[8.75rem]">{clickId.note}</p>
          </div>
        )}

        {utms.length > 0 && (
          <div className="space-y-1.5 border-t pt-4">
            <p className="mb-2 text-sm font-medium">Campaign tags</p>
            {utms.map(({ label, value }) => (
              <Row key={label} label={label} value={value} />
            ))}
          </div>
        )}

        {landing && (
          <div className="space-y-2 border-t pt-4">
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm font-medium">Landing page</p>
              <Button variant="ghost" size="sm" onClick={copyLandingUrl}>
                {copied ? (
                  <Check className="mr-1 h-3.5 w-3.5" />
                ) : (
                  <Copy className="mr-1 h-3.5 w-3.5" />
                )}
                {copied ? 'Copied' : 'Copy URL'}
              </Button>
            </div>

            <Collapsible open={expanded} onOpenChange={setExpanded}>
              <CollapsibleTrigger asChild>
                <button className="flex w-full items-center gap-1 text-left text-sm hover:underline">
                  {expanded ? (
                    <ChevronDown className="h-4 w-4 shrink-0" />
                  ) : (
                    <ChevronRight className="h-4 w-4 shrink-0" />
                  )}
                  <span className="break-all">{landing.summary}</span>
                </button>
              </CollapsibleTrigger>

              <CollapsibleContent className="pt-2">
                {/* The whole URL, verbatim. Everything above is derived from it. */}
                <pre className="max-h-40 overflow-auto whitespace-pre-wrap break-all rounded-md bg-muted p-3 text-xs">
                  {landing.full}
                </pre>
              </CollapsibleContent>
            </Collapsible>
          </div>
        )}

        {otherIds.length > 0 && (
          <div className="space-y-1.5 border-t pt-4">
            <p className="mb-2 text-sm font-medium">Other platforms</p>
            {otherIds.map(({ label, value }) => (
              <Row
                key={label}
                label={label}
                value={<span className="break-all font-mono text-xs">{value}</span>}
              />
            ))}
            <p className="text-xs text-muted-foreground">
              Captured for reference. Only Google conversions are uploaded.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
